/**
 * FaviconGenerator - Static utility class for generating favicon files from an image
 * 
 * This class handles the generation of favicons from both raster (PNG, JPEG, etc.) and vector (SVG) input files.
 * 
 * Key differences in SVG vs Raster handling:
 * - SVG files: Rendered at ultra-high resolution (4096×4096) with aspect ratio preservation and transparent padding.
 *              This ensures the entire vector graphic is preserved without cropping, maintaining quality when downscaled.
 * - Raster files: Center-cropped to square format by clipping excess width or height from the center point.
 *                 This is necessary because raster images can't be "padded" without introducing visible borders.
 * 
 * The two-stage process:
 * 1. loadAndPrepareImage(): Creates a square canvas appropriate for the input type
 * 2. generatePngFavicon(): Applies multi-step high-quality downscaling to create each favicon size
 * 
 * Supports PNG and ICO output formats with client-side processing using the Canvas API.
 */

import JSZip from "jszip";
import type { GeneratedFavicon } from "./types";

export class FaviconGenerator {
    /**
     * Load an image file and convert it to a square canvas
     * 
     * This method handles both SVG and raster images with different strategies:
     * 
     * SVG Strategy (Padding):
     * - Renders the SVG at 4096×4096 resolution (very high to preserve vector quality)
     * - Scales the SVG to fit within the square while maintaining aspect ratio
     * - Centers the scaled SVG with transparent padding to make it square
     * - Result: Full SVG visible, no cropping, transparent padding around edges if needed
     * 
     * Raster Strategy (Cropping):
     * - Determines the smaller dimension (width or height)
     * - Crops from the center to create a square
     * - For landscape images: crops left and right edges
     * - For portrait images: crops top and bottom edges
     * - Result: Center portion of image extracted as a square
     * 
     * @param file The image file to load (SVG, PNG, JPEG, WebP, BMP, TIFF, GIF)
     * @returns Object containing the square canvas and whether it was an SVG input
     */
    static async loadAndPrepareImage(file: File): Promise<{ canvas: HTMLCanvasElement, isSvg: boolean }> {
        const img = new Image();
        const isSvg = file.type === "image/svg+xml";
        
        const imageLoadPromise = new Promise<{ canvas: HTMLCanvasElement, isSvg: boolean }>((resolve, reject) => {
            img.onload = () => {
                // SVG Path: Render at ultra-high resolution with padding to preserve full vector graphic
                if (isSvg) {
                    const canvas = document.createElement("canvas");
                    const ctx = canvas.getContext("2d");
                    if (!ctx) {
                        reject(new Error("Failed to get canvas context"));
                        return;
                    }

                    // Use 4096×4096 as the render target for maximum quality
                    // This high resolution ensures vector sharpness is maintained when downscaling to smaller favicon sizes
                    const targetSize = 4096;
                    canvas.width = targetSize;
                    canvas.height = targetSize;

                    // Enable high-quality rendering
                    ctx.imageSmoothingEnabled = true;
                    ctx.imageSmoothingQuality = "high";
                    
                    // Calculate scaling to fit SVG within square canvas while maintaining aspect ratio
                    // For a 200×100 SVG: scale = min(4096/200, 4096/100) = min(20.48, 40.96) = 20.48
                    // Result: 4096×2048 scaled image centered in 4096×4096 canvas with 1024px padding top/bottom
                    const scale = Math.min(targetSize / img.width, targetSize / img.height);
                    const scaledWidth = img.width * scale;
                    const scaledHeight = img.height * scale;
                    
                    // Center the image in the canvas with transparent padding around it
                    const offsetX = (targetSize - scaledWidth) / 2;
                    const offsetY = (targetSize - scaledHeight) / 2;
                    
                    // Draw the SVG scaled and centered - transparent areas around it become the padding
                    ctx.drawImage(img, offsetX, offsetY, scaledWidth, scaledHeight);

                    resolve({ canvas, isSvg: true });
                } else {
                    // Raster Path: Create square canvas with center crop
                    // Determine the dimension to use (the smaller of width/height)
                    const size = Math.min(img.width, img.height);
                    const canvas = document.createElement("canvas");
                    canvas.width = size;
                    canvas.height = size;

                    const ctx = canvas.getContext("2d");
                    if (!ctx) {
                        reject(new Error("Failed to get canvas context"));
                        return;
                    }

                    // Calculate center crop offsets
                    // For a 1200×800 image: size=800, offsetX=(1200-800)/2=200, offsetY=0
                    // Result: Extract pixels from (200,0) to (1000,800) to create 800×800 square
                    const offsetX = (img.width - size) / 2;
                    const offsetY = (img.height - size) / 2;

                    // Enable high-quality image smoothing
                    ctx.imageSmoothingEnabled = true;
                    ctx.imageSmoothingQuality = "high";

                    // Draw center-cropped portion of the image
                    // drawImage(source, sx, sy, sWidth, sHeight, dx, dy, dWidth, dHeight)
                    ctx.drawImage(img, offsetX, offsetY, size, size, 0, 0, size, size);

                    resolve({ canvas, isSvg: false });
                }
            };
            img.onerror = () => reject(new Error("Failed to load image"));
        });
        img.src = URL.createObjectURL(file);

        const result = await imageLoadPromise;
        URL.revokeObjectURL(img.src);
        return result;
    }

    /**
     * Generate a PNG favicon at the specified size with high quality scaling
     * 
     * Uses multi-step downscaling when the source is significantly larger than the target.
     * This progressive approach produces better quality than a single large downscale operation.
     * 
     * For example, scaling from 4096×4096 to 16×16:
     * - Step 1: 4096 → 2048
     * - Step 2: 2048 → 1024
     * - Step 3: 1024 → 512
     * - Step 4: 512 → 256
     * - Step 5: 256 → 128
     * - Step 6: 128 → 64
     * - Step 7: 64 → 32
     * - Step 8: 32 → 16
     * 
     * This is particularly important for SVG-sourced images rendered at 4096×4096.
     * 
     * @param sourceCanvas The square source canvas (from loadAndPrepareImage)
     * @param size Target size in pixels (e.g., 16, 32, 180, 192, 512)
     * @param size Target size in pixels
     * @returns Blob containing the PNG image
     */
    static async generatePngFavicon(sourceCanvas: HTMLCanvasElement, size: number): Promise<Blob> {
        const canvas = document.createElement("canvas");
        canvas.width = size;
        canvas.height = size;

        const ctx = canvas.getContext("2d");
        if (!ctx) {
            throw new Error("Failed to get canvas context");
        }

        // Enable high-quality image smoothing
        ctx.imageSmoothingEnabled = true;
        ctx.imageSmoothingQuality = "high";

        // Use multi-step scaling for better quality when downscaling significantly
        if (sourceCanvas.width > size * 1.5) {
            let tempCanvas = sourceCanvas;
            let currentSize = sourceCanvas.width;

            // Scale down by half repeatedly until close to target size
            while (currentSize > size * 1.5) {
                const intermediateSize = Math.max(Math.floor(currentSize / 2), size);
                const intermediateCanvas = document.createElement("canvas");
                const intermediateCtx = intermediateCanvas.getContext("2d");
                if (!intermediateCtx) break;

                intermediateCanvas.width = intermediateSize;
                intermediateCanvas.height = intermediateSize;
                intermediateCtx.imageSmoothingEnabled = true;
                intermediateCtx.imageSmoothingQuality = "high";
                intermediateCtx.drawImage(
                    tempCanvas,
                    0,
                    0,
                    tempCanvas.width,
                    tempCanvas.height,
                    0,
                    0,
                    intermediateSize,
                    intermediateSize
                );

                tempCanvas = intermediateCanvas;
                currentSize = intermediateSize;
            }

            // Final scaling to exact target size
            ctx.drawImage(tempCanvas, 0, 0, size, size);
        } else {
            // Single-step scaling for small size differences
            ctx.drawImage(sourceCanvas, 0, 0, size, size);
        }

        // Convert to blob with maximum quality
        return new Promise<Blob>((resolve, reject) => {
            canvas.toBlob((blob) => {
                if (blob) {
                    resolve(blob);
                } else {
                    reject(new Error("Failed to create PNG blob"));
                }
            }, "image/png", 1.0);
        });
    }

    /**
     * Generate an ICO file (using PNG format)
     * Creates a 32x32 PNG saved as .ico - modern browsers accept this
     * @param sourceCanvas The square source canvas
     * @returns Blob containing the ICO file
     */
    static async generateIcoFavicon(sourceCanvas: HTMLCanvasElement): Promise<Blob> {
        // Reuse the high-quality PNG generation for 32x32
        return this.generatePngFavicon(sourceCanvas, 32);
    }

    /**
     * Generate all favicon files from a source image
     * 
     * This is the main entry point for favicon generation. It handles both SVG and raster inputs
     * by first preparing a square canvas using loadAndPrepareImage(), then generating each required
     * favicon size through progressive downscaling.
     * 
     * Process:
     * 1. Load and prepare the image as a square canvas (SVG: padded at 4096×4096, Raster: center-cropped)
     * 2. Generate PNG files at each required size using multi-step downscaling
     * 3. Generate ICO file (32×32 PNG saved as .ico)
     * 4. Return array of all generated files ready for ZIP packaging
     * 
     * @param file The source image file (SVG recommended for best quality)
     * @param onProgress Optional progress callback receiving values from 0-100
     * @returns Array of generated favicon files with filenames and blob data
     */
    static async generateAllFavicons(
        file: File,
        onProgress?: (progress: number) => void
    ): Promise<GeneratedFavicon[]> {
        const results: GeneratedFavicon[] = [];

        try {
            // Load and prepare image - SVG gets padded at 4096×4096, raster gets center-cropped to square
            if (onProgress) onProgress(20);
            const { canvas: squareCanvas } = await this.loadAndPrepareImage(file);

            // Generate each favicon size
            const sizes = [
                { filename: "android-chrome-192x192.png", size: 192 },
                { filename: "android-chrome-512x512.png", size: 512 },
                { filename: "apple-touch-icon.png", size: 180 },
                { filename: "favicon-16x16.png", size: 16 },
                { filename: "favicon-32x32.png", size: 32 },
            ];

            const totalSteps = sizes.length + 1; // +1 for ICO generation
            let currentStep = 0;

            // Generate PNG files
            for (const config of sizes) {
                const blob = await this.generatePngFavicon(squareCanvas, config.size);
                results.push({
                    filename: config.filename,
                    blob,
                    size: config.size,
                });
                currentStep++;
                if (onProgress) onProgress(20 + (currentStep / totalSteps) * 70);
            }

            // Generate ICO file
            const icoBlob = await this.generateIcoFavicon(squareCanvas);
            results.push({
                filename: "favicon.ico",
                blob: icoBlob,
                size: 32,
            });
            currentStep++;
            if (onProgress) onProgress(20 + (currentStep / totalSteps) * 70);

            if (onProgress) onProgress(100);

            return results;
        } catch (error) {
            throw new Error(`Failed to generate favicons: ${error instanceof Error ? error.message : "Unknown error"}`);
        }
    }

    /**
     * Generate site.webmanifest content
     * @returns JSON string for site.webmanifest
     */
    static generateWebManifest(): string {
        const manifest = {
            name: "",
            short_name: "",
            icons: [
                {
                    src: "/android-chrome-192x192.png",
                    sizes: "192x192",
                    type: "image/png",
                },
                {
                    src: "/android-chrome-512x512.png",
                    sizes: "512x512",
                    type: "image/png",
                },
            ],
            theme_color: "#ffffff",
            background_color: "#ffffff",
            display: "standalone",
        };

        return JSON.stringify(manifest, null, 2);
    }

    /**
     * Create a ZIP file containing all favicon files
     * @param favicons Array of generated favicon files
     * @returns Blob containing the ZIP file
     */
    static async createZipFile(favicons: GeneratedFavicon[]): Promise<Blob> {
        console.log("createZipFile called with", favicons.length, "favicons");

        const zip = new JSZip();

        // Add all favicon files
        for (const favicon of favicons) {
            console.log("Adding to zip:", favicon.filename);
            zip.file(favicon.filename, favicon.blob);
        }

        // Add site.webmanifest
        const manifestContent = this.generateWebManifest();
        zip.file("site.webmanifest", manifestContent);

        console.log("Generating ZIP blob...");
        // Generate ZIP blob
        const zipBlob = await zip.generateAsync({ type: "blob" });
        console.log("ZIP blob generated, size:", zipBlob.size);
        return zipBlob;
    }

    /**
     * Download a blob as a file
     * @param blob The blob to download
     * @param filename The filename to use
     */
    static downloadBlob(blob: Blob, filename: string): void {
        console.log("downloadBlob called with filename:", filename, "size:", blob.size);
        const url = URL.createObjectURL(blob);
        const a = document.createElement("a");
        a.href = url;
        a.download = filename;
        document.body.appendChild(a);
        a.click();
        document.body.removeChild(a);
        URL.revokeObjectURL(url);
        console.log("Download triggered");
    }
}
